/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import type {
  AccountMeta,
  AccountSignerMeta,
  Address,
  FixedSizeCodec,
  FixedSizeDecoder,
  FixedSizeEncoder,
  Instruction,
  InstructionWithAccounts,
  InstructionWithData,
  ReadonlyAccount,
  ReadonlySignerAccount,
  ReadonlyUint8Array,
  TransactionSigner,
  WritableAccount,
  WritableSignerAccount,
} from "@solana/kit";
import type { ResolvedInstructionAccount } from "@solana/program-client-core";
import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
} from "@solana/kit";
import { getAccountMetaFactory } from "@solana/program-client-core";
import { WHIRLPOOL_PROGRAM_ADDRESS } from "../programs/index.js";

export const INITIALIZE_TOKEN_BADGE_DISCRIMINATOR: ReadonlyUint8Array =
  new Uint8Array([253, 77, 205, 95, 27, 224, 89, 223]);

export function getInitializeTokenBadgeDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_TOKEN_BADGE_DISCRIMINATOR,
  );
}

export type InitializeTokenBadgeInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountWhirlpoolsConfig extends string | AccountMeta = string,
  TAccountWhirlpoolsConfigExtension extends string | AccountMeta = string,
  TAccountTokenBadgeAuthority extends string | AccountMeta = string,
  TAccountTokenMint extends string | AccountMeta = string,
  TAccountTokenBadge extends string | AccountMeta = string,
  TAccountFunder extends string | AccountMeta = string,
  TAccountSystemProgram extends
    | string
    | AccountMeta = "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountWhirlpoolsConfig extends string
        ? ReadonlyAccount<TAccountWhirlpoolsConfig>
        : TAccountWhirlpoolsConfig,
      TAccountWhirlpoolsConfigExtension extends string
        ? ReadonlyAccount<TAccountWhirlpoolsConfigExtension>
        : TAccountWhirlpoolsConfigExtension,
      TAccountTokenBadgeAuthority extends string
        ? ReadonlySignerAccount<TAccountTokenBadgeAuthority> &
            AccountSignerMeta<TAccountTokenBadgeAuthority>
        : TAccountTokenBadgeAuthority,
      TAccountTokenMint extends string
        ? ReadonlyAccount<TAccountTokenMint>
        : TAccountTokenMint,
      TAccountTokenBadge extends string
        ? WritableAccount<TAccountTokenBadge>
        : TAccountTokenBadge,
      TAccountFunder extends string
        ? WritableSignerAccount<TAccountFunder> &
            AccountSignerMeta<TAccountFunder>
        : TAccountFunder,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export interface InitializeTokenBadgeInstructionData {
  discriminator: ReadonlyUint8Array;
}

export type InitializeTokenBadgeInstructionDataArgs = {};

export function getInitializeTokenBadgeInstructionDataEncoder(): FixedSizeEncoder<InitializeTokenBadgeInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: INITIALIZE_TOKEN_BADGE_DISCRIMINATOR,
    }),
  );
}

export function getInitializeTokenBadgeInstructionDataDecoder(): FixedSizeDecoder<InitializeTokenBadgeInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getInitializeTokenBadgeInstructionDataCodec(): FixedSizeCodec<
  InitializeTokenBadgeInstructionDataArgs,
  InitializeTokenBadgeInstructionData
> {
  return combineCodec(
    getInitializeTokenBadgeInstructionDataEncoder(),
    getInitializeTokenBadgeInstructionDataDecoder(),
  );
}

export interface InitializeTokenBadgeInput<
  TAccountWhirlpoolsConfig extends string = string,
  TAccountWhirlpoolsConfigExtension extends string = string,
  TAccountTokenBadgeAuthority extends string = string,
  TAccountTokenMint extends string = string,
  TAccountTokenBadge extends string = string,
  TAccountFunder extends string = string,
  TAccountSystemProgram extends string = string,
> {
  whirlpoolsConfig: Address<TAccountWhirlpoolsConfig>;
  whirlpoolsConfigExtension: Address<TAccountWhirlpoolsConfigExtension>;
  tokenBadgeAuthority: TransactionSigner<TAccountTokenBadgeAuthority>;
  tokenMint: Address<TAccountTokenMint>;
  tokenBadge: Address<TAccountTokenBadge>;
  funder: TransactionSigner<TAccountFunder>;
  systemProgram?: Address<TAccountSystemProgram>;
}

export function getInitializeTokenBadgeInstruction<
  TAccountWhirlpoolsConfig extends string,
  TAccountWhirlpoolsConfigExtension extends string,
  TAccountTokenBadgeAuthority extends string,
  TAccountTokenMint extends string,
  TAccountTokenBadge extends string,
  TAccountFunder extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof WHIRLPOOL_PROGRAM_ADDRESS,
>(
  input: InitializeTokenBadgeInput<
    TAccountWhirlpoolsConfig,
    TAccountWhirlpoolsConfigExtension,
    TAccountTokenBadgeAuthority,
    TAccountTokenMint,
    TAccountTokenBadge,
    TAccountFunder,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeTokenBadgeInstruction<
  TProgramAddress,
  TAccountWhirlpoolsConfig,
  TAccountWhirlpoolsConfigExtension,
  TAccountTokenBadgeAuthority,
  TAccountTokenMint,
  TAccountTokenBadge,
  TAccountFunder,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? WHIRLPOOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    whirlpoolsConfig: {
      value: input.whirlpoolsConfig ?? null,
      isWritable: false,
    },
    whirlpoolsConfigExtension: {
      value: input.whirlpoolsConfigExtension ?? null,
      isWritable: false,
    },
    tokenBadgeAuthority: {
      value: input.tokenBadgeAuthority ?? null,
      isWritable: false,
    },
    tokenMint: { value: input.tokenMint ?? null, isWritable: false },
    tokenBadge: { value: input.tokenBadge ?? null, isWritable: true },
    funder: { value: input.funder ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("whirlpoolsConfig", accounts.whirlpoolsConfig),
      getAccountMeta(
        "whirlpoolsConfigExtension",
        accounts.whirlpoolsConfigExtension,
      ),
      getAccountMeta("tokenBadgeAuthority", accounts.tokenBadgeAuthority),
      getAccountMeta("tokenMint", accounts.tokenMint),
      getAccountMeta("tokenBadge", accounts.tokenBadge),
      getAccountMeta("funder", accounts.funder),
      getAccountMeta("systemProgram", accounts.systemProgram),
    ],
    data: getInitializeTokenBadgeInstructionDataEncoder().encode({}),
    programAddress,
  } as InitializeTokenBadgeInstruction<
    TProgramAddress,
    TAccountWhirlpoolsConfig,
    TAccountWhirlpoolsConfigExtension,
    TAccountTokenBadgeAuthority,
    TAccountTokenMint,
    TAccountTokenBadge,
    TAccountFunder,
    TAccountSystemProgram
  >);
}

export interface ParsedInitializeTokenBadgeInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> {
  programAddress: Address<TProgram>;
  accounts: {
    whirlpoolsConfig: TAccountMetas[0];
    whirlpoolsConfigExtension: TAccountMetas[1];
    tokenBadgeAuthority: TAccountMetas[2];
    tokenMint: TAccountMetas[3];
    tokenBadge: TAccountMetas[4];
    funder: TAccountMetas[5];
    systemProgram: TAccountMetas[6];
  };
  data: InitializeTokenBadgeInstructionData;
}

export function parseInitializeTokenBadgeInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeTokenBadgeInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 7) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 7,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      whirlpoolsConfig: getNextAccount(),
      whirlpoolsConfigExtension: getNextAccount(),
      tokenBadgeAuthority: getNextAccount(),
      tokenMint: getNextAccount(),
      tokenBadge: getNextAccount(),
      funder: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getInitializeTokenBadgeInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
